import React from 'react';
import { Route, Switch } from 'react-router-dom';
import loadable from '@loadable/component';

import { MirrorService } from 'services/MirrorService';
import { RoutesRestrictionType } from 'types/enums/routes';

import { useMedia } from 'hooks/useMedia';

import { AppRoute } from 'components/shared/AppRoute';
import { DesktopActivityWrapper } from 'components/shared/DesktopActivityWrapper';

import { LikesAndVisitsLayout } from 'layouts/LikesAndVisitsLayout';

const LandingRoutes = loadable(
  () => import(/* webpackChunkName: "LandingRoutes" */ 'pages/LandingRoutes'),
  {
    resolveComponent: (component) => component.LandingRoutes,
  }
);

const CheckInboxPage = loadable(
  () => import(/* webpackChunkName: "CheckInboxPage" */ 'pages/CheckInboxPage'),
  {
    resolveComponent: (component) => component.CheckInboxPage,
  }
);

const ForgotPasswordPage = loadable(
  () =>
    import(
      /* webpackChunkName: "ForgotPasswordPage" */ 'pages/ForgotPasswordPage'
    ),
  {
    resolveComponent: (component) => component.ForgotPasswordPage,
  }
);

const APILandingRoutes = loadable(
  () =>
    import(/* webpackChunkName: "APILandingRoutes" */ 'pages/APILandingRoutes'),
  {
    resolveComponent: (component) => component.APILandingRoutes,
  }
);

const NotFoundPage = loadable(
  () => import(/* webpackChunkName: "NotFoundPage" */ 'pages/NotFoundPage'),
  {
    resolveComponent: (component) => component.NotFoundPage,
  }
);

const NotificationsPageDesktop = loadable(
  () =>
    import(
      /* webpackChunkName: "NotificationsPageDesktop" */ 'pages/NotificationsPageDesktop'
    ),
  {
    resolveComponent: (component) => component.NotificationsPageDesktop,
  }
);

const NotificationsPageMobile = loadable(
  () =>
    import(
      /* webpackChunkName: "NotificationsPageMobile" */ 'pages/NotificationsPageMobile'
    ),
  {
    resolveComponent: (component) => component.NotificationsPageMobile,
  }
);

const ThreadPage = loadable(
  () => import(/* webpackChunkName: "ThreadPage" */ 'pages/ThreadPage'),
  {
    resolveComponent: (component) => component.ThreadPage,
  }
);

const LikesPageMobile = loadable(
  () =>
    import(/* webpackChunkName: "LikesPageMobile" */ 'pages/LikesPageMobile'),
  {
    resolveComponent: (component) => component.LikesPageMobile,
  }
);

const LikesDesktopPage = loadable(
  () =>
    import(/* webpackChunkName: "LikesPageDesktop" */ 'pages/LikesPageDesktop'),
  {
    resolveComponent: (component) => component.LikesPageDesktop,
  }
);

const SimpleDatesLikesPage = loadable(
  () =>
    import(
      /* webpackChunkName: "SimpleDatesLikesPage" */ 'pages/SimpleDatesLikesPage'
    ),
  {
    resolveComponent: (component) => component.SimpleDatesLikesPage,
  }
);

const NewPasswordPage = loadable(
  () =>
    import(/* webpackChunkName: "NewPasswordPage" */ 'pages/NewPasswordPage'),
  {
    resolveComponent: (component) => component.NewPasswordPage,
  }
);

const LegalPage = loadable(
  () => import(/* webpackChunkName: "LegalPage" */ 'pages/LegalPage'),
  {
    resolveComponent: (component) => component.LegalPage,
  }
);

const MyLikesPageMobile = loadable(
  () =>
    import(
      /* webpackChunkName: "MyLikesPageMobile" */ 'pages/MyLikesPageMobile'
    ),
  {
    resolveComponent: (component) => component.MyLikesPageMobile,
  }
);

const MyLikesPageDesktop = loadable(
  () =>
    import(
      /* webpackChunkName: "MyLikesPageDesktop" */ 'pages/MyLikesPageDesktop'
    ),
  {
    resolveComponent: (component) => component.MyLikesPageDesktop,
  }
);

const VisitsPageMobile = loadable(
  () =>
    import(/* webpackChunkName: "VisitsPageMobile" */ 'pages/VisitsPageMobile'),
  {
    resolveComponent: (component) => component.VisitsPageMobile,
  }
);

const VisitsPageDesktop = loadable(
  () =>
    import(
      /* webpackChunkName: "VisitsPageDesktop" */ 'pages/VisitsPageDesktop'
    ),
  {
    resolveComponent: (component) => component.VisitsPageDesktop,
  }
);

const SimpleDatesVisitsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "SimpleDatesVisitsPage" */ 'pages/SimpleDatesVisitsPage'
    ),
  {
    resolveComponent: (component) => component.SimpleDatesVisitsPage,
  }
);

const UserProfilePage = loadable(
  () =>
    import(/* webpackChunkName: "UserProfilePage" */ 'pages/UserProfilePage'),
  {
    resolveComponent: (component) => component.UserProfilePage,
  }
);

const EditProfilePage = loadable(
  () =>
    import(/* webpackChunkName: "EditProfilePage" */ 'pages/EditProfilePage'),
  {
    resolveComponent: (component) => component.EditProfilePage,
  }
);

const TermsPrivacyPage = loadable(
  () =>
    import(/* webpackChunkName: "TermsPrivacyPage" */ 'pages/TermsPrivacyPage'),
  {
    resolveComponent: (component) => component.TermsPrivacyPage,
  }
);

const SettingsPage = loadable(
  () => import(/* webpackChunkName: "SettingsPage" */ 'pages/SettingsPage'),
  {
    resolveComponent: (component) => component.SettingsPage,
  }
);

const FinderPage = loadable(
  () => import(/* webpackChunkName: "FinderPage" */ 'pages/FinderPage'),
  {
    resolveComponent: (component) => component.FinderPage,
  }
);

const MessengerPage = loadable(
  () => import(/* webpackChunkName: "MessengerPage" */ 'pages/MessengerPage'),
  {
    resolveComponent: (component) => component.MessengerPage,
  }
);

const MyProfileCompletionPage = loadable(
  () =>
    import(
      /* webpackChunkName: "MyProfileCompletionPage" */ 'pages/MyProfileCompletionPage'
    ),
  {
    resolveComponent: (component) => component.MyProfileCompletionPage,
  }
);

const MyProfilePage = loadable(
  () => import(/* webpackChunkName: "MyProfilePage" */ 'pages/MyProfilePage'),
  {
    resolveComponent: (component) => component.MyProfilePage,
  }
);

const UpgradePage = loadable(
  () =>
    import(
      /* webpackChunkName: "UpgradePage" */ 'pages/UpgradePage/UpgradePageGuard'
    )
);

const PaymentPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PaymentPage" */ 'pages/PaymentPage/PaymentPageGuard'
    ),
  {
    resolveComponent: (component) => component.PaymentPageGuard,
  }
);

const PaymentPhotoPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PaymentPhotoPage" */ 'pages/PaymentPhotoPage/PaymentPhotoPageGuard'
    ),
  {
    resolveComponent: (component) => component.PaymentPhotoPageGuard,
  }
);

const PaymentSuccessPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PaymentSuccessPage" */ 'pages/PaymentSuccessPage'
    ),
  {
    resolveComponent: (component) => component.PaymentSuccessPage,
  }
);

const PaymentDowngradeSuccessPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PaymentDowngradeSuccessPage" */ 'pages/PaymentDowngradeSuccessPage'
    ),
  {
    resolveComponent: (component) => component.PaymentDowngradeSuccessPage,
  }
);

const PaymentFailPage = loadable(
  () =>
    import(/* webpackChunkName: "PaymentFailPage" */ 'pages/PaymentFailPage'),
  {
    resolveComponent: (component) => component.PaymentFailPage,
  }
);

const CreditPaymentPage = loadable(
  () =>
    import(
      /* webpackChunkName: "CreditPaymentPage" */ 'pages/CreditPaymentPage'
    ),
  {
    resolveComponent: (component) => component.CreditPaymentPage,
  }
);

const VipPaymentPage = loadable(
  () => import(/* webpackChunkName: "VipPaymentPage" */ 'pages/VipPaymentPage'),
  {
    resolveComponent: (component) => component.VipPaymentPage,
  }
);

const LoginPage = loadable(
  () => import(/* webpackChunkName: "LoginPage" */ 'pages/LoginPage'),
  {
    resolveComponent: (component) => component.LoginPage,
  }
);

const IndexPage = loadable(
  () =>
    import(
      /* webpackChunkName: "IndexPage" */ 'pages/IndexPage/IndexPageGuard'
    ),
  {
    resolveComponent: (component) => component.IndexPageGuard,
  }
);

const PromoteVipPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PromoteVipPage" */ 'pages/PromoteVipPage/PromoteVipPageGuard'
    ),
  {
    resolveComponent: (component) => component.PromoteVipPageGuard,
  }
);

const PaymentStatusPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PaymentStatusPage" */ 'pages/PaymentStatusPage'
    ),
  {
    resolveComponent: (component) => component.PaymentStatusPage,
  }
);

const FeaturedPage = loadable(
  () => import(/* webpackChunkName: "FeaturedPage" */ 'pages/FeaturedPage'),
  {
    resolveComponent: (component) => component.FeaturedPage,
  }
);

const BuyStickerPage = loadable(
  () => import(/* webpackChunkName: "BuyStickerPage" */ 'pages/BuyStickerPage'),
  {
    resolveComponent: (component) => component.BuyStickerPage,
  }
);

const CreditPackagesPage = loadable(
  () =>
    import(
      /* webpackChunkName: "CreditPackagesPage" */ 'pages/CreditPackagesPage'
    ),
  {
    resolveComponent: (component) => component.CreditPackagesPage,
  }
);

const UserVideoPage = loadable(
  () => import(/* webpackChunkName: "UserVideoPage" */ 'pages/UserVideoPage'),
  {
    resolveComponent: (component) => component.UserVideoPage,
  }
);

const MatchesPageMobile = loadable(
  () =>
    import(
      /* webpackChunkName: "MatchesPageMobile" */ 'pages/MatchesPageMobile'
    ),
  {
    resolveComponent: (component) => component.MatchesPageMobile,
  }
);

const MatchesDesktopPage = loadable(
  () =>
    import(
      /* webpackChunkName: "MatchesPageDesktop" */ 'pages/MatchesPageDesktop'
    ),
  {
    resolveComponent: (component) => component.MatchesPageDesktop,
  }
);

const TransferAuthPage = loadable(
  () =>
    import(
      /* webpackChunkName: "TransferAuthPage" */ 'pages/TransferAuthPage/TransferAuthPageGuard'
    ),
  {
    resolveComponent: (components) => components.TransferAuthPageGuard,
  }
);

const UpgradeTrialPage = loadable(
  () =>
    import(
      /* webpackChunkName: "UpgradeTrialPage" */ 'pages/UpgradeTrialPage/UpgradeTrialPageGuard'
    ),
  {
    resolveComponent: (components) => components.UpgradeTrialPageGuard,
  }
);

const MembershipPage = loadable(
  () => import(/* webpackChunkName: "MembershipPage" */ 'pages/MembershipPage'),
  {
    resolveComponent: (components) => components.MembershipPage,
  }
);

const ContactsPage = loadable(
  () => import(/* webpackChunkName: "ContactsPage" */ 'pages/ContactsPage'),
  {
    resolveComponent: (components) => components.ContactsPage,
  }
);

const DataPrivacyPage = loadable(
  () =>
    import(/* webpackChunkName: "DataPrivacyPage" */ 'pages/DataPrivacyPage'),
  {
    resolveComponent: (components) => components.DataPrivacyPage,
  }
);

const CookieSettings = loadable(
  () => import(/* webpackChunkName: "CookieSettings" */ 'pages/CookieSettings'),
  {
    resolveComponent: (components) => components.CookieSettings,
  }
);

const HidePage = loadable(
  () => import(/* webpackChunkName: "HidePage" */ 'pages/HidePage'),
  {
    resolveComponent: (components) => components.HidePage,
  }
);

export const Routes = () => {
  const { isMobile } = useMedia();

  return (
    <Switch>
      {/* BOT DETECT */}
      <Route path="/hide" component={HidePage} />
      {/* LANDINGS */}
      <Route path="/landing" component={LandingRoutes} />
      {/*  PUBLIC ROUTES */}
      <Route path="/jump([0-9]+)" component={APILandingRoutes} />
      <AppRoute path="/login" component={LoginPage} isRestricted />
      <AppRoute path="/check-inbox" component={CheckInboxPage} isRestricted />
      <AppRoute path="/terms" component={LegalPage} />
      <AppRoute path="/compliance-statement" component={LegalPage} />
      <AppRoute
        path="/(privacy|billing|refund|tracking)-policy"
        component={LegalPage}
      />
      <AppRoute path="/mail-disclosure" component={LegalPage} />
      <AppRoute path="/washington-health" component={LegalPage} />
      <AppRoute path="/complaints-policy" component={LegalPage} />
      <AppRoute path="/content-removal-policy" component={LegalPage} />
      <AppRoute path="/consent-policy" component={LegalPage} />
      <AppRoute path="/data-privacy" component={DataPrivacyPage} />
      <AppRoute path="/cookie-settings" component={CookieSettings} exact />
      <AppRoute path="/one-click/auth" component={TransferAuthPage} />
      <AppRoute
        path="/forgot-password"
        component={ForgotPasswordPage}
        isRestricted
      />
      <AppRoute
        path="/new-password/:code"
        component={NewPasswordPage}
        isRestricted
      />
      <AppRoute
        path="/:type(welcome)?"
        component={IndexPage}
        isRestricted
        exact
      />
      {MirrorService.isSimpleDates && (
        <AppRoute
          path="/membership"
          component={MembershipPage}
          isRestricted
          exact
        />
      )}
      {MirrorService.isSimpleDates && (
        <AppRoute
          path="/contacts"
          component={ContactsPage}
          isRestricted
          exact
        />
      )}
      {/* PRIVATE ROUTES */}
      <AppRoute
        path={['/finder', '/finder/videocast']}
        component={FinderPage}
        isPrivate
        exact
      />
      <AppRoute path="/myprofile" component={MyProfilePage} isPrivate exact />
      <AppRoute
        path="/myprofile/completion"
        component={MyProfileCompletionPage}
        isPrivate
        isRestrictedDevice
        restrictedType={RoutesRestrictionType.DesktopRestricted}
        exact
      />
      <AppRoute path="/terms-privacy" component={TermsPrivacyPage} isPrivate />
      <AppRoute path="/settings" component={SettingsPage} isPrivate />
      <AppRoute path="/featured" component={FeaturedPage} isPrivate />
      <AppRoute path="/promote" component={PromoteVipPage} isPrivate exact />
      <AppRoute path="/threads" component={MessengerPage} isPrivate exact />
      <AppRoute
        path="/threads/:contactId"
        component={isMobile ? ThreadPage : MessengerPage}
        isPrivate
      />
      <AppRoute
        path="/(upgrade|upgrade_premium)"
        component={UpgradePage}
        isPrivate
      />
      <AppRoute path="/upgrade_trial" component={UpgradeTrialPage} isPrivate />

      <AppRoute path="/payment/fail" component={PaymentFailPage} isPrivate />
      <AppRoute
        path="/payment/success"
        component={PaymentSuccessPage}
        isPrivate
      />
      <AppRoute
        path="/payment/success_downgrade"
        component={PaymentDowngradeSuccessPage}
        isPrivate
      />
      <AppRoute
        path="/payment/status"
        component={PaymentStatusPage}
        isPrivate
      />
      <AppRoute path="/payment-photo" component={PaymentPhotoPage} isPrivate />
      <AppRoute
        path="/payments_credit/:packageId"
        component={CreditPaymentPage}
        isPrivate
      />
      <AppRoute
        path="/payments_vip/:packageId"
        component={VipPaymentPage}
        isPrivate
      />
      <AppRoute
        path={['/payment/:packageId', '/payment']}
        component={PaymentPage}
        isPrivate
      />
      <AppRoute
        path="/buy-sticker-packs/:stickerPackId(\d+)"
        component={BuyStickerPage}
        isPrivate
      />
      <AppRoute
        path="/:userSlug(user[0-9]+)"
        component={UserProfilePage}
        isPrivate
        exact
      />
      <AppRoute
        path="/:userSlug(user[0-9]+)/edit"
        component={EditProfilePage}
        isPrivate
        exact
      />
      <AppRoute
        path="/:userSlug(user[0-9]+)/videos/:videoId?"
        component={UserVideoPage}
        isPrivate
        exact
      />
      <AppRoute
        path="/credit-upgrade"
        component={CreditPackagesPage}
        isPrivate
        exact
      />
      <AppRoute
        path="/matches"
        component={MatchesPageMobile}
        isPrivate
        isRestrictedDevice
        restrictedType={RoutesRestrictionType.DesktopRestricted}
      />
      <AppRoute
        path="/notifications"
        component={NotificationsPageMobile}
        isPrivate
        isRestrictedDevice
        restrictedType={RoutesRestrictionType.DesktopRestricted}
      />
      <AppRoute
        path="/mylikes"
        component={MyLikesPageMobile}
        isPrivate
        isRestrictedDevice
        restrictedType={RoutesRestrictionType.DesktopRestricted}
      />
      {!MirrorService.isSimpleDates && (
        <Route exact path={['/visits', '/likes']}>
          <Switch>
            <AppRoute
              path="/visits"
              component={VisitsPageMobile}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.DesktopRestricted}
            />
            <AppRoute
              path="/likes"
              component={LikesPageMobile}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.DesktopRestricted}
            />
          </Switch>
        </Route>
      )}
      <Route
        path={[
          '/activities/notifications',
          '/activities/visits',
          '/activities/likes',
          '/activities/matches',
          '/activities/mylikes',
        ]}
        exact
      >
        <DesktopActivityWrapper>
          <Switch>
            <AppRoute
              path="/activities/notifications"
              component={NotificationsPageDesktop}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.MobileRestricted}
            />
            <AppRoute
              path="/activities/visits"
              component={VisitsPageDesktop}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.MobileRestricted}
            />
            <AppRoute
              path="/activities/likes"
              component={LikesDesktopPage}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.MobileRestricted}
            />
            <AppRoute
              path="/activities/matches"
              component={MatchesDesktopPage}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.MobileRestricted}
            />
            <AppRoute
              path="/activities/mylikes"
              component={MyLikesPageDesktop}
              isPrivate
              isRestrictedDevice
              restrictedType={RoutesRestrictionType.MobileRestricted}
            />
          </Switch>
        </DesktopActivityWrapper>
      </Route>
      {MirrorService.isSimpleDates && (
        <Route path={['/likes', '/visits']} exact>
          <LikesAndVisitsLayout>
            <Switch>
              <AppRoute
                path="/likes"
                component={SimpleDatesLikesPage}
                isPrivate
                exact
              />
              <AppRoute
                path="/visits"
                component={SimpleDatesVisitsPage}
                isPrivate
                exact
              />
            </Switch>
          </LikesAndVisitsLayout>
        </Route>
      )}
      <AppRoute path="*" component={NotFoundPage} />
    </Switch>
  );
};
